import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What types of properties does TurnCraft Property Solutions service?',
    answer: 'We work with a wide range of property types throughout Northern New Jersey, including single-family rental homes, multi-unit apartment buildings, condominiums, office spaces, retail locations, and mixed-use commercial buildings. Whether you manage one unit or an entire portfolio, our team is equipped to handle projects of all sizes. We are equally comfortable with a quick one-bedroom apartment turn between tenants and a large-scale commercial interior repaint across multiple floors.',
  },
  {
    question: 'How quickly can TurnCraft start a job after I reach out?',
    answer: 'In most cases, we can schedule a walkthrough and provide a written estimate within 24 to 48 hours of your inquiry. For urgent situations — such as a tenant who has moved out unexpectedly and you need the unit turned quickly — we do our best to prioritize your job and get a crew in as fast as our schedule allows. We understand that vacant units cost you money, and we treat turnaround time as a critical priority for every client.',
  },
  {
    question: 'What does an apartment turn service include?',
    answer: 'A standard apartment turn with TurnCraft typically includes a full interior repaint of all walls and ceilings, touch-up or full replacement of trim paint as needed, a comprehensive deep clean of the entire unit including kitchen appliances inside and out, bathrooms, floors, baseboards, windows, and closets. We can also handle minor touch-ups and repairs before painting if needed. Every turn is customized to the actual condition of the unit — we assess during the walkthrough and give you an itemized quote for exactly what needs to be done.',
  },
  {
    question: 'Is TurnCraft licensed and insured in New Jersey?',
    answer: 'Yes, absolutely. TurnCraft Property Solutions carries full general liability insurance and workers\' compensation coverage for all employees. We are licensed to operate as a home improvement contractor in New Jersey. Before any project begins, we are happy to provide certificates of insurance naming you or your management company as an additional insured — a requirement many property management companies have for their contractors, and something we are very accustomed to providing.',
  },
  {
    question: 'What areas of New Jersey do you serve?',
    answer: 'We are based in Florham Park, NJ and serve a broad service area across Northern New Jersey, with a primary focus on Morris County, Essex County, and Union County. In Morris County, we regularly work in Florham Park, Madison, Chatham, Morris Plains, Morristown, Parsippany, East Hanover, Whippany, Hanover Township, Cedar Knolls, Pine Brook, Denville, Mountain Lakes, and Convent Station. In Essex County, we serve Livingston, Millburn, Short Hills, Roseland, West Orange, Maplewood, and South Orange. In Union County, we work in Summit, New Providence, Berkeley Heights, Springfield, and Westfield. If your property is not on this list, contact us anyway — we may still be able to accommodate you.',
  },
  {
    question: 'Do you offer recurring or contract-based cleaning and maintenance services?',
    answer: 'Yes. For property managers who need ongoing janitorial, cleaning, or maintenance support, we offer recurring service agreements tailored to your needs. This can include regularly scheduled cleaning of common areas, lobbies, hallways, and shared facilities in multi-unit buildings, as well as standing agreements to handle apartment turns on an as-needed basis as units turn over throughout the year. Reach out to discuss what a recurring arrangement might look like for your specific property.',
  },
  {
    question: 'What paint brands and products do you use?',
    answer: 'We primarily use professional-grade paints from leading brands including Sherwin-Williams and Benjamin Moore, selected based on the specific requirements of each surface and environment. For rental properties, we typically recommend durable, washable finishes — such as eggshell or satin — that hold up well to tenant use and are easy to clean between turns. We are happy to match existing colors, recommend new ones, or work with a color specified by the property owner or management company.',
  },
  {
    question: 'How do I get a price estimate for my project?',
    answer: 'The best way to get an accurate estimate is to contact us and schedule a walkthrough of your property. Pricing varies based on the size of the unit or space, the condition of the walls and surfaces, the scope of services needed (painting only, cleaning only, or a full turn), and current scheduling. After the walkthrough, we provide a written, itemized quote with no hidden fees. For straightforward projects, we can sometimes provide ballpark pricing over the phone or email based on square footage and a description of the scope.',
  },
];

const faqSchemaJsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 lg:py-32 bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: faqSchemaJsonLd }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
          {/* Left sticky header */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-question-answer-line text-orange-500"></i>
              <span className="text-xs uppercase tracking-wider text-orange-500 font-semibold">
                FAQ
              </span>
            </div>
            <h2
              className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Common Questions Answered
            </h2>
            <p className="text-base text-gray-600 leading-relaxed mb-8">
              Everything you need to know about working with TurnCraft Property Solutions — from how we price our services to what\'s included in a standard apartment turn. Can\'t find your answer here? Give us a call.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:scale-105 transition-transform whitespace-nowrap cursor-pointer"
            >
              Ask Us Directly
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* FAQ Accordion */}
          <div className="lg:col-span-3 space-y-3">
            {faqs.map((faq, index) => (
              <article
                key={index}
                className="border border-gray-100 rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full text-left px-7 py-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  aria-expanded={openIndex === index}
                >
                  <h4 className="text-sm font-semibold text-gray-900 leading-snug">
                    {faq.question}
                  </h4>
                  <span className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-gray-100">
                    <i
                      className={`text-gray-600 text-sm transition-transform duration-200 ${
                        openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </span>
                </button>
                {openIndex === index && (
                  <div className="px-7 pb-6">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
